import React from 'react';
import StaticContentPage from '../components/StaticContentPage';
import { Shield, Info, Database, Eye, Lock, Share2, UserCheck, RefreshCw } from 'lucide-react';

const PrivacySection = ({ icon: Icon, title, children }) => (
  <div className="mb-12">
    <div className="flex items-center gap-3 mb-6">
      <div className="p-2 rounded-xl bg-brand-indigo/10 text-brand-indigo">
        <Icon size={20} />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 tracking-tight">{title}</h2>
    </div>
    <div className="text-gray-600 leading-relaxed space-y-4 ml-1">
      {children}
    </div>
  </div>
);

const PrivacyPolicy = () => {
  return (
    <StaticContentPage title="Privacy Policy" lastUpdated="May 14, 2026">
      <div className="max-w-3xl mb-12">
        <p className="text-xl font-medium text-gray-700 leading-relaxed mb-6">
          Welcome to Chooziq.
        </p>
        <p className="text-gray-600 leading-relaxed">
          This Privacy Policy explains how we collect, use, store, disclose, and protect your information when you use the Chooziq platform, website, communities, live rooms, and related services (collectively, the “Services”).
        </p>
      </div>

      <PrivacySection icon={Info} title="1. About Chooziq">
        <p>Chooziq is a realtime interaction and collaboration platform designed for communities, educational institutions, organizations, events, workshops, webinars, conferences, and professional groups. Our Services enable users to host live interactive sessions, participate in realtime discussions, and collaborate through brainstorming and engagement tools.</p>
      </PrivacySection>

      <PrivacySection icon={Database} title="2. Information We Collect">
        <div className="space-y-6">
          <div>
            <h4 className="font-bold text-gray-900 mb-2">A. Information You Provide</h4>
            <p className="text-sm">Includes your name, email, profile biography, image, organization details, messages, discussions, questions, brainstorming contributions, and poll responses.</p>
          </div>
          <div>
            <h4 className="font-bold text-gray-900 mb-2">B. Realtime Interaction Data</h4>
            <p className="text-sm">Participation records including questions submitted, voting, poll participation, brainstorming contributions, and audience reactions.</p>
          </div>
          <div>
            <h4 className="font-bold text-gray-900 mb-2">C. Automatically Collected Information</h4>
            <p className="text-sm">IP address, device info, browser type, usage data, log information, and session duration.</p>
          </div>
        </div>
      </PrivacySection>

      <PrivacySection icon={Eye} title="3. How We Use Information">
        <p>We use collected information to provide and maintain the Services, enable realtime interactions, improve platform functionality, personalize content, and ensure security and policy enforcement.</p>
      </PrivacySection>

      <PrivacySection icon={RefreshCw} title="4. Persistent Content and Session History">
        <p>Chooziq is designed to preserve engagement beyond live sessions. Discussions, Q&A, brainstorm boards, and poll results may remain accessible after a session ends based on community settings and moderator controls.</p>
      </PrivacySection>

      <PrivacySection icon={Lock} title="5. Public Communities and User Content">
        <p>Certain communities and live sessions may be publicly accessible. Content you share publicly may be viewed by other users and indexed within the platform.</p>
      </PrivacySection>

      <PrivacySection icon={Share2} title="6. How We Share Information">
        <p>We do not sell personal information to third parties. Information may be shared with trusted service providers, community administrators, organizers, or when required for legal and security reasons.</p>
      </PrivacySection>

      <PrivacySection icon={Shield} title="7. Data Security">
        <p>We implement administrative and technical measures to protect your data. However, no method of transmission is completely secure, and users are responsible for their account credentials.</p>
      </PrivacySection>

      <PrivacySection icon={UserCheck} title="8. User Rights and Choices">
        <p>Depending on your location, you may have rights to access, correct, or request deletion of your data. Requests can be submitted through our support channels.</p>
      </PrivacySection>

      <div className="bg-gray-50 p-10 rounded-[40px] border border-gray-100 mt-16 text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h3>
        <p className="text-gray-600 mb-6">If you have questions regarding this Privacy Policy, please contact us.</p>
        <div className="flex flex-col items-center gap-2">
          <a href="mailto:privacy@chooziq.com" className="text-lg font-bold text-brand-indigo hover:underline">privacy@chooziq.com</a>
        </div>
      </div>
    </StaticContentPage>
  );
};

export default PrivacyPolicy;
